/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { FunctionExecutingDialogComponent } from "app/components/infrastructure/dialogs/function-executing-dialog.component";
import { DraggableDirective } from "app/directives/draggable.directive";
import { Function } from "app/models/function";
import { FunctionParameterData } from "app/models/function-parameter-data";
import { ObjectCategory } from "app/models/object-category";
import { ObjectInstance } from "app/models/object-instance";
import { PropertyPatch } from "app/models/property-patch";
import { ObjectService } from "app/services/object.service";

declare var $: any;

/**
 * Popup Dialog used to execute Function Action(s) on the Locate Page for Assets.
 */
@Component({
    selector: 'ef-instance-function-popup',
    templateUrl: './instance-function-dialog.component.html'
})
export class InstanceFunctionDialogComponent {

    //Draggable Directive added to that the popup is draggable.  Referenced so that it can be reset when the popup is hidden
    @ViewChild(DraggableDirective) mDraggableDirective: DraggableDirective;

    // Output used to throw an Event with the updated Asset information after successfully executing the function
    @Output()
    functionCompletedSuccessfullyEvent = new EventEmitter<ObjectInstance>();

    // Reference to the Asset the Function is being executed on
    instance: ObjectInstance = null;

    // Reference to the Function that is being executed on the Asset
    function: Function = null;

    // The Parameter Data being displayed in the UI for the Function to be executed.  Allows the User to enter values for the Parameters.
    functionData: FunctionParameterData[] = [];

    @ViewChild('functionExecutingDialog')
    mFunctionExecutingDialog: FunctionExecutingDialogComponent;


    constructor(
        private objectService: ObjectService
    ) { }


    /**
     * Called to set the Instance and Function to execute on the given Instance.
     * This builds up the FunctionParameterData[] required to call the Function (if needed). 
     */
    public setInstanceAndFunction(instance: ObjectInstance, functionKey: string): void {
        // Set the Instance on which the Function is being executed
        this.instance = instance;

        // Set the Function reference to execute on the Asset
        this.function = this.instance?.object?.functions?.find(f => f.key == functionKey);

        // Build the FunctionParmaterData[] to display to the User so they can enter values for the Parameters used by the Function
        const functionData: FunctionParameterData[] = [];
        if (this.function != null) {
            for (let parameter of this.function.parameters) {
                const datum = new FunctionParameterData();
                datum.functionParameter = parameter;
                datum.value = "";
                functionData.push(datum);
            }
        }
        this.functionData = functionData;
    }
    
    /**
     * Called by the UI to get the Title for this InstanceFunctionDialog
     */
    public getDialogTitle(): string {
        switch (this.instance?.object?.category) {
            case ObjectCategory.ASSET: {
                return `Execute Function: '${this.function?.name}' on Asset: '${this.instance?.name}'?`;
            }
            case ObjectCategory.PEOPLE: {
                return `Execute Function: '${this.function?.name}' on Person: '${this.instance?.name}'?`;
            }
            default: {
                return `Execute Function: '${this.function?.name}' on Instance: '${this.instance?.name}'?`;
            }
        }
    }

    /**
     * Called by the UI to show this InstanceFunctionDialog.
     * 
     * This also resets the "Drag" position so that it appears in the same position
     * every time it is opened. 
     */
    public show(): void {
        // Reset the Draggable Directive so that the Dialog pops up in the same place (Horizontally centered at the top of the screen)
        this.mDraggableDirective.reset();
        $("#instanceFunctionDialog").show();
    }

    /**
     * Called by the UI to hide this InstanceFunctionDialog.
     */
    public hide(): void {
        $("#instanceFunctionDialog").hide();
    }

    /**
     * Called by the UI to execute the Function on the Instance using the current FunctionParameterData[] values.
     * 
     * This calls the API, then if successful obtains a fresh copy of the Asset to emit in the "Success" event.
     */
    public async execute(): Promise<void> {
        this.functionStartingToExecute(this.function);

        // Build the PropertyPatch[] used in the API call to execute the Function
        const data: PropertyPatch[] = [];
        for (let parameterData of this.functionData) {
            if (parameterData.value != "") {
                const propertyPatch = new PropertyPatch();
                propertyPatch.property = parameterData.functionParameter.key;
                propertyPatch.value = parameterData.value;
                data.push(propertyPatch);
            }
        }

        // Call the API to execute the Function
        let rv: any = null;
        try {
            rv = await this.objectService
                .executeFunction(this.instance.id, this.function.id, data);
        } catch (error) {
            this.functionFinishedExecutingWithError(error?.error);
            return;
        }

        this.functionFinishedExecutingSuccessfully(rv);

        // Obtain a "fresh" version of the Instance whose Function was just called / completed successfully
        let instance: ObjectInstance = null;
        try {
            instance = await this.objectService.getObjectInstanceForId(this.instance.id);
        } catch (error) {
            console.error("Problem obtaining refreshed Instance after successfully executing function.", error);
            return;
        }
        
        // Sort the Instance's Data so it's in order using the Property Order (seeing this come back in a random order)
        instance.data.sort((a, b) => a.property.order - b.property.order);

        // Emit a refreshed version of the Instance with any updated information as a "Success Event"
        this.functionCompletedSuccessfullyEvent.emit(instance);
    }
    


    

  functionStartingToExecute(func: Function): void {
    this.mFunctionExecutingDialog.showDialogStartExecutingFunction(func);
  }

  functionFinishedExecutingSuccessfully(response: any): void {
  this.mFunctionExecutingDialog.showFunctionExecutionSuccess(response);
}

  functionFinishedExecutingWithError(error: string): void {
    this.mFunctionExecutingDialog.showFunctionExecutionError(error);
  }

}
